import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Dropdown,
  Upload,
  App,
  Tooltip,
} from "antd";
import type { MenuProps } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import * as Yup from "yup";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/checkmark.svg";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import {
  getDirectionSelectOptions,
  DIRECTION_OPTIONS,
} from "../../utils/directionOptions";
import {
  getBaseKnowledgeThunk,
  getToolsThunk,
  createAgentThunk,
} from "../../store/actions/agent.action";
import { useAppDispatch, useAppSelector } from "../../store";
import { ReactComponent as CheckCircle } from "../../assets/icons/checkCircle.svg";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { createBaseKnowledgeThunk } from "../../store/actions/agent.action";
import ToastMessage from "../../utils/toastMessage";
import VoicePreviewButton from "../../components/VoicePreviewButton";
import { AGENTS_VOICE, TOOL_TYPE_GROUPS } from "../../models/agent.model";

const { Option } = Select;
const OptGroup = Select.OptGroup;

interface CreateAgentModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreateSuccess: () => void;
}

const CreateAgentModal: React.FC<CreateAgentModalProps> = ({
  visible,
  onCancel,
  onCreateSuccess,
}) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const selectedTools = Form.useWatch("tool", form);
  const [isStepValid, setIsStepValid] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isFormProvided, setIsFormProvided] = useState(false);
  const [tools, setTools] = useState<any[]>([]);
  const [baseKnowledge, setBaseKnowledge] = useState<any[]>([]);
  const [knowledgeFiles, setKnowledgeFiles] = useState<File[]>([]);
  const [selectedBaseKnowledge, setSelectedBaseKnowledge] = useState<string[]>(
    []
  );
  const [isKbDragging, setIsKbDragging] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const greetingRef = useRef<any>(null);
  const systemRef = useRef<any>(null);
  const voicemailRef = useRef<any>(null);
  const endCallRef = useRef<any>(null);
  const [greetingVariables, setGreetingVariables] = useState<string[]>([]);
  const [systemVariables, setSystemVariables] = useState<string[]>([]);
  const [voicemailVariables, setVoicemailVariables] = useState<string[]>([]);
  const [endCallVariables, setEndCallVariables] = useState<string[]>([]);
  const [greetingCaret, setGreetingCaret] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [systemCaret, setSystemCaret] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [voicemailCaret, setVoicemailCaret] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [endCallCaret, setEndCallCaret] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [currentVoice, setCurrentVoice] = useState<string>(
    AGENTS_VOICE[0].value
  );

  /**
   * Matches tokens shaped like {{{variable}}} and captures the inner text.
   */
  const VARIABLE_REGEX = /{{{([\s\S]*?)}}}/g;
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  // Validation schemas for each step
  const stepSchemas = [
    Yup.object().shape({
      agent_name: Yup.string().required(
        t("agents.agentForm.validation.agentNameRequired")
      ),
      agent_description: Yup.string().notRequired(),
      direction: Yup.string().required(
        t("agents.agentForm.validation.directionRequired")
      ),
      voice: Yup.string().notRequired(),
    }),
    Yup.object().shape({
      greeting_message: Yup.string().required(
        t("agents.agentForm.validation.greetingMessageRequired")
      ),
      system_message: Yup.string().required(
        t("agents.agentForm.validation.systemMessageRequired")
      ),
      voicemail_message: Yup.string().notRequired(),
      ending_message: Yup.string().notRequired(),
      tool: Yup.array().of(Yup.mixed()).notRequired(),
    }),
    // Step 3: Knowledge base (optional)
    Yup.object().shape({
      knowledge_base_ids: Yup.array().of(Yup.mixed()).notRequired(),
    }),
  ];

  const fetchBaseKnowledge = async () => {
    try {
      const res = await dispatch(getBaseKnowledgeThunk()).unwrap();
      setBaseKnowledge(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTools = async () => {
    try {
      const res = await dispatch(getToolsThunk()).unwrap();
      setTools(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Extract unique variable names from a textarea value.
   */
  const parseVariables = (text?: string): string[] => {
    if (!text) return [];
    const vars: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = VARIABLE_REGEX.exec(text)) !== null) {
      const name = (match[1] || "").trim();
      vars.push(name || "");
    }
    return Array.from(new Set(vars));
  };

  /**
   * Refresh chip lists for both textareas from current form values.
   */
  const updateVariableChips = () => {
    const {
      greeting_message,
      system_message,
      voicemail_message,
      ending_message,
    } = form.getFieldsValue([
      "greeting_message",
      "system_message",
      "voicemail_message",
      "ending_message",
    ]);
    setGreetingVariables(parseVariables(greeting_message));
    setSystemVariables(parseVariables(system_message));
    setVoicemailVariables(parseVariables(voicemail_message));
    setEndCallVariables(parseVariables(ending_message));
  };

  /**
   * Get the underlying HTMLTextAreaElement from antd's TextArea ref.
   */
  const getTextAreaEl = (ref: any): HTMLTextAreaElement | null => {
    if (!ref) return null;
    return (
      ref?.resizableTextArea?.textArea ||
      ref?.textArea ||
      ref?.input ||
      ref ||
      null
    );
  };

  const groupedTools = useMemo(() => {
    const groups: Record<string, any[]> = {
      general: [],
      customize: [],
      internal: [],
    };

    tools.forEach((tool) => {
      const rawType = tool.type as string;
      const type = String(rawType).toLowerCase();
      if (groups[type]) {
        groups[type].push(tool);
      }
    });

    return groups;
  }, [tools]);

  /**
   * Determine if the selected tools require an extra birthday column.
   * Matches against display_name/function_name to cover API variations.
   */
  const needsBirthdayColumn = useMemo(() => {
    const picked = selectedTools || [];
    return picked.some((fnName: string) => {
      const tool = tools.find((t) => t.function_name === fnName);
      return tool?.function_name === "logCustomerInCall";
    });
  }, [selectedTools, tools]);

  /** Headers used for the downloadable CSV template. */
  const templateHeaders = useMemo(() => {
    const headers = [
      "phone_number",
      ...greetingVariables,
      ...systemVariables,
      ...voicemailVariables,
      ...endCallVariables,
    ];
    if (needsBirthdayColumn) headers.push("birthday");
    return Array.from(new Set(headers));
  }, [
    greetingVariables,
    systemVariables,
    voicemailVariables,
    endCallVariables,
    needsBirthdayColumn,
  ]);

  /** Escape a string for use in a RegExp literal. */
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  /**
   * Remove all occurrences of a specific {{{variable}}} token from a textarea value
   * and normalize surrounding whitespace/punctuation.
   */
  const removeVariableFromText = (text: string, varName: string): string => {
    const safeVarName = escapeRegExp(varName);
    const token = new RegExp(`{{{\\s*${safeVarName}\\s*}}}`, "g");
    let result = text.replace(token, "");
    result = result.replace(/\s{2,}/g, " ").replace(/\s+([,.!?;:])/g, "$1");
    return result.trim();
  };

  type MessageFieldName =
    | "greeting_message"
    | "system_message"
    | "voicemail_message"
    | "ending_message";

  const getRefForField = (fieldName: MessageFieldName) => {
    if (fieldName === "greeting_message") return greetingRef.current;
    if (fieldName === "system_message") return systemRef.current;
    if (fieldName === "voicemail_message") return voicemailRef.current;
    return endCallRef.current;
  };

  const setCaretForField = (
    fieldName: MessageFieldName,
    caret: { start: number; end: number } | null
  ) => {
    if (fieldName === "greeting_message") setGreetingCaret(caret);
    else if (fieldName === "system_message") setSystemCaret(caret);
    else if (fieldName === "voicemail_message") setVoicemailCaret(caret);
    else setEndCallCaret(caret);
  };

  const getCaretForField = (fieldName: MessageFieldName) => {
    if (fieldName === "greeting_message") return greetingCaret;
    if (fieldName === "system_message") return systemCaret;
    if (fieldName === "voicemail_message") return voicemailCaret;
    return endCallCaret;
  };

  /**
   * Trim spaces inside tokens (after {{{ and before }}}) and keep the caret in a sensible place.
   * Called onChange so users can type naturally without extra inner spaces.
   */
  const sanitizeVariableSpaces = (
    fieldName: MessageFieldName,
    incomingValue: string,
    caretStart: number | null | undefined
  ) => {
    let value = incomingValue;

    let newCaret = caretStart ?? null;
    if (newCaret !== null) {
      const regex = VARIABLE_REGEX;
      let match: RegExpExecArray | null;
      let removedBefore = 0;
      while ((match = regex.exec(value)) !== null) {
        const fullStart = match.index;
        const inner = match[1] ?? "";
        const innerStart = fullStart + 3;
        const innerEnd = innerStart + inner.length;
        const leadingSpaces = inner.match(/^\s+/)?.[0].length ?? 0;
        const trailingSpaces = inner.match(/\s+$/)?.[0].length ?? 0;
        const totalRemoved = leadingSpaces + trailingSpaces;
        if (totalRemoved === 0) continue;

        if (newCaret > innerEnd) {
          removedBefore += totalRemoved;
          continue;
        }
        if (newCaret >= innerStart && newCaret <= innerEnd) {
          if (newCaret <= innerStart + leadingSpaces) {
            newCaret = innerStart;
          } else if (newCaret > innerEnd - trailingSpaces) {
            newCaret = innerEnd - trailingSpaces;
          } else {
            newCaret = newCaret - leadingSpaces;
          }
        }
      }
      if (newCaret !== null) newCaret = newCaret - removedBefore;
    }

    value = value.replace(
      VARIABLE_REGEX,
      (_m, inner: string) => `{{{${(inner || "").trim()}}}}`
    );

    form.setFieldsValue({ [fieldName]: value });
    // variable chips will update via onValuesChange

    const ref = getRefForField(fieldName);
    const el = getTextAreaEl(ref);
    if (el && newCaret !== null) {
      requestAnimationFrame(() => {
        try {
          el.setSelectionRange(newCaret!, newCaret!);
        } catch {}
      });
    }
  };

  /** Wire textarea change to sanitizer to keep tokens clean. */
  const handleTextAreaChange = (
    fieldName: MessageFieldName,
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const ref = getRefForField(fieldName);
    const el = getTextAreaEl(ref);
    const caret = el?.selectionStart ?? null;
    sanitizeVariableSpaces(fieldName, e.target.value, caret);
  };

  /** Keep variable chips in sync when either textarea value changes through the form. */
  const handleValuesChange = (changedValues: any, allValues: any) => {
    if (
      Object.prototype.hasOwnProperty.call(changedValues, "greeting_message") ||
      Object.prototype.hasOwnProperty.call(changedValues, "system_message") ||
      Object.prototype.hasOwnProperty.call(changedValues, "voicemail_message") ||
      Object.prototype.hasOwnProperty.call(changedValues, "ending_message")
    ) {
      setGreetingVariables(parseVariables(allValues.greeting_message));
      setSystemVariables(parseVariables(allValues.system_message));
      setVoicemailVariables(parseVariables(allValues.voicemail_message));
      setEndCallVariables(parseVariables(allValues.ending_message));

      // Re-validate current step when textarea values change
      if (current === 1) {
        stepSchemas[current]
          .validate(allValues, { abortEarly: false })
          .then(() => setIsStepValid(true))
          .catch(() => setIsStepValid(false));
      }
    }

    // Track voice changes for preview button
    if (Object.prototype.hasOwnProperty.call(changedValues, "voice")) {
      setCurrentVoice(changedValues.voice || AGENTS_VOICE[0].value);
    }
  };

  /** Remember current caret so clicking menu buttons can still insert at the last position. */
  const rememberCaret = (
    fieldName: MessageFieldName,
    e?: React.SyntheticEvent<HTMLTextAreaElement>
  ) => {
    const target = e?.currentTarget as HTMLTextAreaElement | undefined;
    const el = target || getTextAreaEl(getRefForField(fieldName));
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    setCaretForField(fieldName, { start, end });
  };

  /**
   * Insert a token at the caret. If a variable name is provided, insert {{{name}}} and move caret after it;
   * otherwise insert an empty {{{}}} and move caret inside the braces. Adds spaces before/after when needed.
   */
  const insertVariableAtCaret = (
    fieldName: MessageFieldName,
    variableName?: string
  ) => {
    const ref = getRefForField(fieldName);
    const el = getTextAreaEl(ref);
    if (!el) return;

    const currentValue: string = form.getFieldValue(fieldName) || "";

    const isFocused = document.activeElement === el;
    const remembered = getCaretForField(fieldName);
    const start = isFocused
      ? el.selectionStart ?? currentValue.length
      : remembered?.start ?? currentValue.length;
    const end = isFocused ? el.selectionEnd ?? start : remembered?.end ?? start;
    const prevChar = start > 0 ? currentValue.charAt(start - 1) : "";
    const nextChar = end < currentValue.length ? currentValue.charAt(end) : "";

    // Only add spaces if there are actual characters and they're not whitespace
    const needsSpaceBefore =
      start > 0 && currentValue.length > 0 && !/\s/.test(prevChar);
    const needsSpaceAfter =
      end < currentValue.length &&
      currentValue.length > 0 &&
      !/\s/.test(nextChar);

    const coreToken = `{{{${variableName ?? ""}}}}`;
    const token =
      (needsSpaceBefore ? " " : "") + coreToken + (needsSpaceAfter ? " " : "");
    const newValue =
      currentValue.slice(0, start) + token + currentValue.slice(end);

    form.setFieldsValue({ [fieldName]: newValue });
    // Ensure chips update immediately after programmatic insert
    updateVariableChips();

    // Trigger validation to update isStepValid
    if (current === 1) {
      const values = form.getFieldsValue();
      stepSchemas[current]
        .validate(values, { abortEarly: false })
        .then(() => setIsStepValid(true))
        .catch(() => setIsStepValid(false));
    }

    const newCaretPos = variableName
      ? start +
        (needsSpaceBefore ? 1 : 0) +
        coreToken.length +
        (needsSpaceAfter ? 1 : 0)
      : start + (needsSpaceBefore ? 1 : 0) + 3;
    requestAnimationFrame(() => {
      el.focus();
      try {
        el.setSelectionRange(newCaretPos, newCaretPos);
      } catch {}
      setCaretForField(fieldName, { start: newCaretPos, end: newCaretPos });
    });
  };

  /** Build the Add variable dropdown for a specific field. */
  const getDropdownMenuForField = (
    fieldName: MessageFieldName
  ): MenuProps => {
    const items: MenuProps["items"] = [
      {
        type: "group",
        label: t("agents.agentForm.customVariables"),
        children: [
          { key: "__add_new__", label: t("agents.agentForm.addVariable") },
        ],
      },
    ];
    return {
      items,
      onClick: ({ key }) => {
        if (key === "__add_new__") insertVariableAtCaret(fieldName);
        else insertVariableAtCaret(fieldName, String(key));
      },
    };
  };

  /** Remove a variable from both the chips and the textarea content for the given field. */
  const handleRemoveVariable = (
    fieldName: MessageFieldName,
    variableName: string
  ) => {
    const currentValue: string = form.getFieldValue(fieldName) || "";
    const newValue = removeVariableFromText(currentValue, variableName);
    form.setFieldsValue({ [fieldName]: newValue });
    updateVariableChips();

    // Trigger validation to update isStepValid
    if (current === 1) {
      const values = form.getFieldsValue();
      stepSchemas[current]
        .validate(values, { abortEarly: false })
        .then(() => setIsStepValid(true))
        .catch(() => setIsStepValid(false));
    }
  };

  const handleFieldsChange = () => {
    const values = form.getFieldsValue();
    setIsFormProvided(
      !!(
        values.agent_name ||
        values.agent_description ||
        values.greeting_message ||
        values.system_message ||
        values.voicemail_message ||
        values.ending_message ||
        values.tool ||
        values.direction
      )
    );

    // Validate current step and update step validity
    stepSchemas[current]
      .validate(values, { abortEarly: false })
      .then(() => setIsStepValid(true))
      .catch(() => setIsStepValid(false));
  };

  const handleKnowledgeFileChange = (files: File[]) => {
    setKnowledgeFiles(files);
    form.setFields([{ name: "knowledge_base_ids", errors: [] }]);
    stepSchemas[2]
      .validate(
        {
          knowledge_base_ids: files,
        },
        { abortEarly: false }
      )
      .then(() => setIsStepValid(true))
      .catch(() => setIsStepValid(false));
  };

  // Merge newly selected files into the existing list (dedupe by name/size/lastModified)
  const addKnowledgeFiles = (incoming: File[]) => {
    if (!incoming || incoming.length === 0) return;
    const signature = (f: File) =>
      `${f.name}|${f.size}|${(f as any).lastModified ?? ""}`;
    const existingMap = new Map(knowledgeFiles.map((f) => [signature(f), f]));
    for (const f of incoming) {
      const sig = signature(f);
      if (!existingMap.has(sig)) existingMap.set(sig, f);
    }
    const merged = Array.from(existingMap.values());
    handleKnowledgeFileChange(merged);
  };

  // Remove a single file by index
  const removeKnowledgeFile = (index: number) => {
    const next = knowledgeFiles.filter((_, i) => i !== index);
    handleKnowledgeFileChange(next);
  };

  const handleCancel = () => {
    if (isFormProvided) {
      setShowUnsavedModal(true);
      return;
    }
    onCancel();
    setCurrent(0);
    setKnowledgeFiles([]);
    form.resetFields();
    setGreetingVariables([]);
    setSystemVariables([]);
    setVoicemailVariables([]);
    setEndCallVariables([]);
  };

  const handleLeave = () => {
    setShowUnsavedModal(false);
    onCancel();
    setCurrent(0);
    setKnowledgeFiles([]);
    form.resetFields();
    setGreetingVariables([]);
    setSystemVariables([]);
    setVoicemailVariables([]);
    setEndCallVariables([]);
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedModal(false);
  };

  const next = () => setCurrent(current + 1);
  const prev = () => setCurrent(current - 1);

  const handleSubmit = () => {
    form.validateFields().then(async (values) => {
      // Treat all parsed variables as custom variables
      const customVariablesObj: { [key: string]: string } = {};
      let customIndex = 1;

      const allVariables = Array.from(
        new Set([
          ...greetingVariables,
          ...systemVariables,
          ...voicemailVariables,
          ...endCallVariables,
        ])
      );

      allVariables.forEach((variable) => {
        customVariablesObj[`custom_variable_${customIndex}`] = variable;
        customIndex++;
      });
      setLoading(true);
      let knowledge_base_ids: string[] = [];
      if (knowledgeFiles.length > 0) {
        const uploadResult: any = await dispatch(
          createBaseKnowledgeThunk({
            files: knowledgeFiles,
          })
        ).unwrap();
        knowledge_base_ids = uploadResult?.data?.knowledge_base_ids || [];
      }

      const {
        voice,
        greeting_message,
        system_message,
        voicemail_message,
        ending_message,
        ...rest
      } = values;

      await dispatch(
        createAgentThunk({
          ...rest,
          system_message,
          knowledge_base_ids: [...selectedBaseKnowledge, ...knowledge_base_ids],
          voice_configs: {
            voice,
            greeting_message,
            voicemail_message,
            ...(user?.is_super_admin && ending_message
              ? { ending_message }
              : {}),
          },
          parameters: {
            custom_variables: customVariablesObj,
          },
        })
      ).unwrap();
      toast.success(t("agents.agentCreatedSuccessfully"), 5, <CheckCircle />);

      onCreateSuccess();
      form.resetFields();
      setLoading(false);
      setKnowledgeFiles([]);
      setGreetingVariables([]);
      setSystemVariables([]);
      setVoicemailVariables([]);
      setEndCallVariables([]);
      setCurrent(0);
    });
  };

  useEffect(() => {
    const validateStep = async () => {
      const values = form.getFieldsValue();
      try {
        await stepSchemas[current].validate(values, { abortEarly: false });
        setIsStepValid(true);
      } catch {
        setIsStepValid(false);
      }
    };
    validateStep();
  }, [form, current]);

  useEffect(() => {
    fetchTools();
    fetchBaseKnowledge();
  }, []);

  return (
    <>
      {!showUnsavedModal && (
        <Modal
          title={null}
          open={visible}
          onCancel={handleCancel}
          footer={null}
          width={900}
          closable={false}
        >
          <ModalHeader>
            <ModalTitle>{t("agents.createAgentModal.title")}</ModalTitle>
            <CustomStepper>
              {[0, 1, 2].map((step, idx) => (
                <React.Fragment key={step}>
                  <StepCircleCustom
                    $active={current === step}
                    $completed={current > step}
                  >
                    {current > step ? <CheckmarkIcon /> : step + 1}
                  </StepCircleCustom>
                  {idx < 2 && <StepLine $completed={current > step} />}
                </React.Fragment>
              ))}
            </CustomStepper>
            <CloseButton type="text" onClick={handleCancel}>
              ✕
            </CloseButton>
          </ModalHeader>

          <Form
            form={form}
            labelCol={{
              span: 5,
              style: { fontWeight: 600, fontSize: 16 },
            }}
            labelAlign="left"
            colon={false}
            onFieldsChange={handleFieldsChange}
            onValuesChange={handleValuesChange}
            initialValues={{
              voice: AGENTS_VOICE[0].value,
              direction: DIRECTION_OPTIONS.OUTBOUND,
            }}
            style={{ minHeight: 500, margin: "24px 0" }}
          >
            {/* Step 1: Basic Information */}
            <div style={{ display: current === 0 ? "block" : "none" }}>
              <h3
                style={{
                  color: theme.colors.grey1,
                  marginBottom: 24,
                  fontSize: 24,
                  fontWeight: 400,
                }}
              >
                {t("agents.createAgentModal.step1")}
              </h3>
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.agentForm.agentName")}
                    <Tooltip title={t("agents.agentForm.tooltips.agentName")}>
                      <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                    </Tooltip>
                  </span>
                }
                name="agent_name"
              >
                <Input
                  allowClear
                  size="large"
                  placeholder={t("agents.agentForm.enterAgentName")}
                />
              </Form.Item>
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.agentForm.agentDescription")}
                    <Tooltip
                      title={t("agents.agentForm.tooltips.agentDescription")}
                    >
                      <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                    </Tooltip>
                  </span>
                }
                name="agent_description"
              >
                <Input
                  allowClear
                  size="large"
                  placeholder={t("agents.agentForm.enterDescription")}
                />
              </Form.Item>
              <VoiceSelectContainer>
                <Form.Item
                  label={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {t("agents.agentForm.voice")}
                      <Tooltip title={t("agents.agentForm.tooltips.voice")}>
                        <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                      </Tooltip>
                    </span>
                  }
                  name="voice"
                >
                  <Select style={{ width: `calc(100% - 48px)` }} size="large">
                    {AGENTS_VOICE.map((voice, index) => (
                      <Option key={index} value={voice.value}>
                        {t(`agents.agentForm.${voice.name}Voice`)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <VoicePreviewButtonContainer>
                  <VoicePreviewButton
                    voiceType={
                      AGENTS_VOICE.find((voice) => voice.value === currentVoice)
                        ?.name || ""
                    }
                  />
                </VoicePreviewButtonContainer>
              </VoiceSelectContainer>
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.agentForm.direction")}
                    <Tooltip title={t("agents.agentForm.tooltips.direction")}>
                      <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                    </Tooltip>
                  </span>
                }
                name="direction"
              >
                <Select
                  style={{ width: "50%" }}
                  size="large"
                  placeholder={t("agents.agentForm.selectDirection")}
                >
                  {getDirectionSelectOptions(t).map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Step 2: Message Settings & Tools */}
            <div style={{ display: current === 1 ? "block" : "none" }}>
              <h3
                style={{
                  color: theme.colors.grey1,
                  marginBottom: 24,
                  fontSize: 24,
                  fontWeight: 400,
                }}
              >
                {t("agents.createAgentModal.step3")}
              </h3>
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.agentForm.tools")}
                    <Tooltip title={t("agents.agentForm.tooltips.tools")}>
                      <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                    </Tooltip>
                  </span>
                }
                name="tool"
                extra={t("agents.agentForm.toolsLimitHint")}
              >
                <Select
                  size="large"
                  placeholder={t("agents.agentForm.toolsPlaceholder")}
                  mode="multiple"
                  maxCount={10}
                  maxTagCount="responsive"
                  maxTagPlaceholder={(omittedValues) => (
                    <span>+{omittedValues.length}</span>
                  )}
                >
                  {TOOL_TYPE_GROUPS.map((group) => {
                    const list = groupedTools[group.value] || [];
                    // Only super admin users can see internal tools
                    if (group.value === "internal" && !user?.is_super_admin) {
                      return null;
                    }
                    if (!list.length) return null;

                    const labelNode = (
                      <span style={{ fontSize: 14 }}>{group.label}</span>
                    );

                    return (
                      <OptGroup key={group.value} label={labelNode}>
                        {list
                          .filter(
                            (tool: any) => tool?.function_name !== "endCall"
                          )
                          .map((tool: any) => {
                            const isVerifyCustomer =
                              tool?.function_name === "logCustomerInCall";
                            return (
                              <Option key={tool._id} value={tool.function_name}>
                                {isVerifyCustomer ? (
                                  <Tooltip title="HIPPA compliance - Verify the customer's identity by their birthday.">
                                    <span>{tool.display_name}</span>
                                  </Tooltip>
                                ) : (
                                  tool.display_name
                                )}
                              </Option>
                            );
                          })}
                      </OptGroup>
                    );
                  })}
                </Select>
              </Form.Item>
              {/* Greeting message textarea + variable controls */}
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.agentForm.greetingMessage")}
                    <Tooltip
                      title={t("agents.agentForm.tooltips.greetingMessage")}
                    >
                      <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                    </Tooltip>
                  </span>
                }
                name="greeting_message"
                style={{ marginBottom: 4 }}
              >
                <Input.TextArea
                  allowClear
                  rows={3}
                  placeholder={t("agents.agentForm.enterGreetingMessage")}
                  ref={greetingRef}
                  onChange={(e) => handleTextAreaChange("greeting_message", e)}
                  onSelect={(e) => rememberCaret("greeting_message", e)}
                  onKeyUp={(e) => rememberCaret("greeting_message", e)}
                  onClick={(e) => rememberCaret("greeting_message", e)}
                  onBlur={(e) => rememberCaret("greeting_message", e)}
                />
              </Form.Item>
              <Form.Item
                wrapperCol={{ offset: 5 }}
                style={{ marginBottom: 24 }}
              >
                {/* Variable chips with remove (×) and Add variable dropdown */}
                <ChipContainer>
                  {greetingVariables.map((v, idx) => (
                    <ChipButton key={`${v}-${idx}`}>
                      {v.length > 0 && <span>{v}</span>}
                      <ChipClose
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVariable("greeting_message", v);
                        }}
                      >
                        ×
                      </ChipClose>
                    </ChipButton>
                  ))}
                  <Dropdown
                    trigger={["click"]}
                    menu={getDropdownMenuForField("greeting_message")}
                  >
                    <ChipButton icon={"+"}>
                      {t("agents.agentForm.addVariable")}
                    </ChipButton>
                  </Dropdown>
                </ChipContainer>
              </Form.Item>
              {/* System message textarea + variable controls */}
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.agentForm.systemMessage")}
                    <Tooltip
                      title={t("agents.agentForm.tooltips.systemMessage")}
                    >
                      <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                    </Tooltip>
                  </span>
                }
                name="system_message"
                style={{ marginBottom: 4 }}
              >
                <Input.TextArea
                  allowClear
                  rows={3}
                  placeholder={t("agents.agentForm.systemMessagePlaceholder")}
                  ref={systemRef}
                  onChange={(e) => handleTextAreaChange("system_message", e)}
                  onSelect={(e) => rememberCaret("system_message", e)}
                  onKeyUp={(e) => rememberCaret("system_message", e)}
                  onClick={(e) => rememberCaret("system_message", e)}
                  onBlur={(e) => rememberCaret("system_message", e)}
                />
              </Form.Item>
              <Form.Item wrapperCol={{ offset: 5 }}>
                <ChipContainer>
                  {systemVariables.map((v, idx) => (
                    <ChipButton key={`${v}-${idx}`}>
                      {v.length > 0 && <span>{v}</span>}
                      <ChipClose
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVariable("system_message", v);
                        }}
                      >
                        ×
                      </ChipClose>
                    </ChipButton>
                  ))}
                  <Dropdown
                    trigger={["click"]}
                    menu={getDropdownMenuForField("system_message")}
                  >
                    <ChipButton icon={"+"}>
                      {t("agents.agentForm.addVariable")}
                    </ChipButton>
                  </Dropdown>
                </ChipContainer>
              </Form.Item>
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.agentForm.voicemailMessage")}
                    <Tooltip
                      title={t("agents.agentForm.tooltips.voicemailMessage")}
                    >
                      <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                    </Tooltip>
                  </span>
                }
                name="voicemail_message"
                style={{ marginBottom: 4 }}
              >
                <Input.TextArea
                  allowClear
                  rows={3}
                  placeholder={t("agents.agentForm.enterVoicemailMessage")}
                  ref={voicemailRef}
                  onChange={(e) => handleTextAreaChange("voicemail_message", e)}
                  onSelect={(e) => rememberCaret("voicemail_message", e)}
                  onKeyUp={(e) => rememberCaret("voicemail_message", e)}
                  onClick={(e) => rememberCaret("voicemail_message", e)}
                  onBlur={(e) => rememberCaret("voicemail_message", e)}
                />
              </Form.Item>
              <Form.Item
                wrapperCol={{ offset: 5 }}
                style={{ marginBottom: 24 }}
              >
                <ChipContainer>
                  {voicemailVariables.map((v, idx) => (
                    <ChipButton key={`${v}-${idx}`}>
                      {v.length > 0 && <span>{v}</span>}
                      <ChipClose
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVariable("voicemail_message", v);
                        }}
                      >
                        ×
                      </ChipClose>
                    </ChipButton>
                  ))}
                  <Dropdown
                    trigger={["click"]}
                    menu={getDropdownMenuForField("voicemail_message")}
                  >
                    <ChipButton icon={"+"}>
                      {t("agents.agentForm.addVariable")}
                    </ChipButton>
                  </Dropdown>
                </ChipContainer>
              </Form.Item>
              {/* End call message textarea + variable controls (super admin only) */}
              {user?.is_super_admin && (
                <>
                  <Form.Item
                    label={
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {t("agents.agentForm.endCallMessage")}
                        <Tooltip
                          title={t("agents.agentForm.tooltips.endCallMessage")}
                        >
                          <InfoCircleOutlined style={{ color: "#bcbcbc" }} />
                        </Tooltip>
                      </span>
                    }
                    name="ending_message"
                    style={{ marginBottom: 4 }}
                  >
                    <Input.TextArea
                      allowClear
                      rows={3}
                      placeholder={t("agents.agentForm.enterEndCallMessage")}
                      ref={endCallRef}
                      onChange={(e) =>
                        handleTextAreaChange("ending_message", e)
                      }
                      onSelect={(e) => rememberCaret("ending_message", e)}
                      onKeyUp={(e) => rememberCaret("ending_message", e)}
                      onClick={(e) => rememberCaret("ending_message", e)}
                      onBlur={(e) => rememberCaret("ending_message", e)}
                    />
                  </Form.Item>
                  <Form.Item
                    wrapperCol={{ offset: 5 }}
                    style={{ marginBottom: 24 }}
                  >
                    <ChipContainer>
                      {endCallVariables.map((v, idx) => (
                        <ChipButton key={`${v}-${idx}`}>
                          {v.length > 0 && <span>{v}</span>}
                          <ChipClose
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveVariable("ending_message", v);
                            }}
                          >
                            ×
                          </ChipClose>
                        </ChipButton>
                      ))}
                      <Dropdown
                        trigger={["click"]}
                        menu={getDropdownMenuForField("ending_message")}
                      >
                        <ChipButton icon={"+"}>
                          {t("agents.agentForm.addVariable")}
                        </ChipButton>
                      </Dropdown>
                    </ChipContainer>
                  </Form.Item>
                </>
              )}
              {/* Variable usage description and CSV template hint */}
              <Form.Item wrapperCol={{ offset: 5, span: 19 }}>
                <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 2 }}>
                  {t("agents.agentForm.notes")}
                </div>
                <div
                  style={{
                    color: theme.colors.grey1,
                    fontSize: 14,
                    marginBottom: 8,
                    lineHeight: 1.6,
                  }}
                >
                  {t("agents.agentForm.variablesDescription")}{" "}
                  <span style={{ fontWeight: 700 }}>
                    {t("agents.agentForm.variableExample")}
                  </span>{" "}
                  {t("agents.agentForm.variablesDescriptionEnd")}
                </div>
                <div
                  style={{
                    color: theme.colors.grey1,
                    fontSize: 14,
                    marginBottom: 8,
                    lineHeight: 1.6,
                  }}
                >
                  {t("agents.agentForm.examples")}{" "}
                  <span style={{ fontWeight: 700 }}>
                    {"{{{phone_number}}}"},
                  </span>{" "}
                  <span style={{ fontWeight: 700 }}>{"{{{first_name}}}"},</span>{" "}
                  <span style={{ fontWeight: 700 }}>{"{{{last_name}}}"}</span>
                </div>
                <div
                  style={{
                    borderRadius: 8,
                    background: "#fff",
                    color: theme.colors.grey1,
                  }}
                >
                  <div style={{ marginBottom: 6 }}>
                    {t("agents.agentForm.templateCsvInstruction")}{" "}
                    <a
                      href={
                        "data:text/csv;charset=utf-8," +
                        encodeURIComponent(templateHeaders.join(",") + "\n")
                      }
                      download="campaign_template.csv"
                    >
                      {t("agents.agentForm.downloadTemplate")}
                    </a>
                  </div>
                </div>
              </Form.Item>
            </div>

            {/* Step 3: Knowledge Base (optional) */}
            <div style={{ display: current === 2 ? "block" : "none" }}>
              <h3
                style={{
                  color: theme.colors.grey1,
                  marginBottom: 24,
                  fontSize: 24,
                  fontWeight: 400,
                }}
              >
                {t("agents.createAgentModal.step4")}
              </h3>
              <Form.Item
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("agents.createAgentModal.documents")}
                    <Tooltip title={t("agents.agentForm.tooltips.documents")}>
                      <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                    </Tooltip>
                  </span>
                }
                name="knowledge_base_ids"
              >
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <Select
                    size="large"
                    mode="multiple"
                    placeholder={t(
                      "agents.createAgentModal.knowledgeBasePlaceholder"
                    )}
                    onChange={(value) => {
                      setSelectedBaseKnowledge(value);
                    }}
                  >
                    {baseKnowledge.map((file: any) => (
                      <Option key={file._id} value={file._id}>
                        {file.file_name || "-"}
                      </Option>
                    ))}
                  </Select>
                  {knowledgeFiles.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <FileChipContainer>
                        <UploadedFileInfoRow>
                          <CheckCircle />
                          <UploadedFileText>
                            {t("agents.createAgentModal.fileUploaded")}
                          </UploadedFileText>
                        </UploadedFileInfoRow>
                        {knowledgeFiles.map((kf, idx) => (
                          <FileChip key={`${kf.name}-${idx}`}>
                            <UploadedFileName>{kf.name}</UploadedFileName>
                            <ChipClose onClick={() => removeKnowledgeFile(idx)}>
                              ×
                            </ChipClose>
                          </FileChip>
                        ))}
                      </FileChipContainer>
                    </div>
                  )}
                  <UploadButtonRow>
                    <Upload
                      name="files"
                      beforeUpload={() => false}
                      accept=".txt,.pdf,.docx,.doc,.csv"
                      multiple
                      fileList={[]}
                      showUploadList={false}
                      onChange={({ fileList }: any) => {
                        const files = fileList
                          .map((f: any) => f.originFileObj)
                          .filter(Boolean);
                        addKnowledgeFiles(files);
                        form.setFields([
                          { name: "knowledge_base_ids", errors: [] },
                        ]);
                      }}
                    >
                      <Button size="large" icon={<UploadOutlined />}>
                        {t("agents.createAgentModal.uploadFile")}
                      </Button>
                    </Upload>
                    <span style={{ color: theme.colors.grey1, fontSize: 14 }}>
                      {t("agents.createAgentModal.allowedFileTypes")}
                    </span>
                  </UploadButtonRow>
                  <DragDropArea
                    onDragOver={() => setIsKbDragging(true)}
                    onDragLeave={() => setIsKbDragging(false)}
                    onDrop={() => setIsKbDragging(false)}
                  >
                    <Upload.Dragger
                      name="files"
                      fileList={[]}
                      beforeUpload={() => false}
                      multiple
                      onChange={({ fileList }: any) => {
                        const files = fileList
                          .map((f: any) => f.originFileObj)
                          .filter(Boolean);
                        addKnowledgeFiles(files);
                        form.setFields([
                          { name: "knowledge_base_ids", errors: [] },
                        ]);
                      }}
                      accept=".txt,.pdf,.docx,.doc,.csv"
                      style={{
                        borderRadius: 8,
                        background: isKbDragging
                          ? theme.colors.green2
                          : theme.colors.grey4,
                        borderColor: isKbDragging
                          ? theme.colors.green1
                          : theme.colors.grey1,
                        borderWidth: 2,
                        transition: "all 0.2s",
                      }}
                      height={120}
                      showUploadList={false}
                    >
                      <div
                        style={{
                          color: isKbDragging
                            ? theme.colors.green1
                            : theme.colors.grey1,
                          fontSize: 15,
                          textAlign: "center",
                        }}
                      >
                        {t("campaignModal.create.dragDropText")}
                        <br />
                        {t("campaignModal.create.dragDropSubtext")}
                      </div>
                    </Upload.Dragger>
                  </DragDropArea>
                </div>
              </Form.Item>
            </div>
          </Form>

          <FooterRow $hasBack={current > 0}>
            {current > 0 && (
              <StyledButton onClick={prev} style={{ marginRight: 8 }}>
                {t("agents.createAgentModal.back")}
              </StyledButton>
            )}
            <div>
              <StyledButton onClick={handleCancel} style={{ marginRight: 8 }}>
                {t("agents.createAgentModal.cancel")}
              </StyledButton>
              {current < 2 && (
                <StyledButton
                  $primary
                  onClick={() => {
                    form.validateFields().then(() => next());
                  }}
                  disabled={!isStepValid}
                >
                  {t("agents.createAgentModal.next")}
                </StyledButton>
              )}
              {current === 2 && (
                <StyledButton
                  $primary
                  onClick={handleSubmit}
                  disabled={!isStepValid || loading}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      {t("agents.createAgentModal.creating")}
                    </>
                  ) : (
                    t("agents.createAgentModal.create")
                  )}
                </StyledButton>
              )}
            </div>
          </FooterRow>
        </Modal>
      )}
      {showUnsavedModal && (
        <UnsavedChangesModal
          open={showUnsavedModal}
          onLeave={handleLeave}
          onCancel={handleUnsavedCancel}
          message={t("agents.createAgentModal.areYouSureLeaveCreation")}
        />
      )}
    </>
  );
};

// Custom Stepper Styles
const CustomStepper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  z-index: 0;
`;

const StepCircleCustom = styled.div<{
  $active?: boolean;
  $completed?: boolean;
}>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid
    ${({ $completed, $active }) =>
      $completed || $active ? theme.colors.green1 : theme.colors.grey1};
  background: ${({ $completed }) =>
    $completed ? theme.colors.green1 : "#fff"};
  color: ${({ $completed, $active }) =>
    $completed ? "#fff" : $active ? theme.colors.green1 : theme.colors.grey1};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  transition: all 0.2s;
  box-sizing: border-box;
`;

const StepLine = styled.div<{
  $completed?: boolean;
}>`
  height: 1px;
  width: 16px;
  background: ${({ $completed }) =>
    $completed ? theme.colors.green1 : theme.colors.grey1};
  transition: background 0.2s;
`;

const StyledButton = styled(Button)<{ $primary?: boolean }>`
  background: ${({ $primary }) =>
    $primary ? theme.colors.green1 : theme.colors.grey3};
  color: ${({ $primary }) => ($primary ? "#fff" : "#000")};
  border: 1px solid
    ${({ $primary }) => ($primary ? theme.colors.green1 : theme.colors.grey2)};
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ $primary }) =>
      $primary ? theme.colors.green1 : theme.colors.grey3} !important;
    color: ${({ $primary }) => ($primary ? "#fff" : "#000")} !important;
    border: 1px solid
      ${({ $primary }) => ($primary ? theme.colors.green1 : theme.colors.grey2)} !important;
  }

  &:disabled {
    background: ${theme.colors.grey2} !important;
    color: #fff !important;
    border: 1px solid ${theme.colors.grey2} !important;
  }
`;

const ChipButton = styled(Button)`
  height: 28px;
  border-radius: 16px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  background: ${theme.colors.grey3};
  border: 1px solid ${theme.colors.grey2};
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const ChipClose = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  border-radius: 50%;
  line-height: 18px;
  cursor: pointer;
`;

// Add styled components for header, title, close button, and footer row
const ModalHeader = styled.div`
  position: relative;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 16px;
  margin: 0 -24px 0 -24px;
  display: flex;
  align-items: center;
  min-height: 56px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  z-index: 1;
  padding: 0px 0 0 24px;
`;

const CloseButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  font-size: 16px;
  color: #8c8c8c;
  z-index: 2;
  padding: 4px 10px;
  margin-right: 24px;
`;

const FooterRow = styled.div<{ $hasBack: boolean }>`
  display: flex;
  justify-content: ${({ $hasBack }) =>
    $hasBack ? "space-between" : "flex-end"};
  border-top: 1px solid #f0f0f0;
  margin: 0 -24px 0 -24px;
  padding: 24px 24px 0 24px;
`;

// Upload UI styled components (match CreateCampaign modal look)
const UploadedFileInfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  height: 30px;
`;

const UploadedFileText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${theme.colors.grey1};
`;

const UploadedFileName = styled.span`
  color: #000;
`;

const UploadButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const DragDropArea = styled.div``;

// Compact chips for selected files
const FileChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${theme.colors.grey3};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 14px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const VoiceSelectContainer = styled.div`
  position: relative;
`;

const VoicePreviewButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9d9d9;
  border-radius: 8px;

  &:hover {
    background: ${theme.colors.grey3};
  }
`;

export default CreateAgentModal;
